import type { GameInfo, GameRelation } from '../SeriesEditor/types'
import type { Coords } from './SeriesSchemeRenderer'
import { gameDateToInt } from '../../common/helpers'

export default class GamesPositioner {
  unprocessedGames: Array<GameInfo> = []
  toRender: Array<Array<{
    game: GameInfo,
    coords: Coords
  }>> = []
  alreadyAssigned: Array<number> = []
  roots: Array<GameInfo> = []
  games: GameInfo[] = []
  gamesRelations: GameRelation[] = []

  constructor(games: GameInfo[], relations: GameRelation[]) {
    this.games = games
    this.gamesRelations = relations
  }

  processGames() {
    this.unprocessedGames = [...this.games]
    let batch = 0
    const mainLine = this.unprocessedGames.filter(game => game.type === 0).sort((game1, game2) => gameDateToInt(game1.release_date) - gameDateToInt(game2.release_date))
    let coords = {
      x: 0,
      y: 0
    }
    mainLine.forEach(game => {
      this.renderNode(game, batch, coords)
      coords.y += 1
    })
    coords = {
      x: 0,
      y: 0
    }
    mainLine.forEach(game => {
      const gameToRender = this.toRender[batch].find(game2 => game2.game.id === game.id)
      if (gameToRender) {
        gameToRender.game = this.packRelations(game)
      }
      coords.y += 1
    })
    ++batch
    if (this.unprocessedGames.length > 0) {
      do {
        const coords = {
          x: 0,
          y: 0
        }
        const game = this.unprocessedGames[0]
        this.renderNode(game, batch, coords)
        const gameToRender = this.toRender[batch].find(game2 => game2.game.id === game.id)
        if (gameToRender) {
          gameToRender.game = this.packRelations(game)
        }
        ++batch
      } while (this.unprocessedGames.length > 0)
    }
    this.normalizeBatches()
    return {
      toRender: this.toRender,
      roots: this.roots
    }
  }

  renderNode = (game: GameInfo, batch: number, coords: Coords) => {
    if (!this.toRender[batch]) {
      this.toRender[batch] = []
    }
    this.alreadyAssigned.push(game.id)
    const index = this.toRender[batch].findIndex(gameInfo => gameInfo.game.id === game.id)
    if (index === -1) {
      this.toRender[batch].push({
        game,
        coords: { ...coords }
      })
    }
  }

  packRelations = (game: GameInfo) => {
    const index = this.unprocessedGames.findIndex(game2 => game2.id === game.id)
    if (index === -1) {
      return
    }
    this.unprocessedGames.splice(index, 1)
    let nodeLinks = this.gamesRelations.filter(relation => relation.first_game_id === game.id)
    const reverseLinks = this.gamesRelations.filter(relation => relation.second_game_id === game.id)
      .map(relation => ({
        first_game_id: relation.second_game_id,
        second_game_id: relation.first_game_id,
        relation_type: -relation.relation_type,
      }))
      .filter(relation => relation.relation_type !== -10)
    nodeLinks = [...nodeLinks, ...reverseLinks]
    if (nodeLinks.length > 0) {
      if (!game.children) {
        game.children = []
      }
      nodeLinks.forEach(relation => {
        let id = relation.first_game_id
        if (id === game.id) {
          id = relation.second_game_id
        }
        if (this.alreadyAssigned.includes(id)) {
          return
        }
        this.alreadyAssigned.push(id)
        const relatedGame = this.games.find(game => game.id === id)
        if (relatedGame) {
          game.children.push(relatedGame)
        }
      })
      game.children.forEach(children => {
        this.packRelations(children)
      })
    }
    return game
  }

  normalizeBatches() {
    this.toRender.forEach(batch => {
      let rootGame = batch[0]
      batch.forEach(gameInfo => {
        if (gameInfo.game.type === 0 // Если это «Игра серии»
          && (
            gameInfo.coords.y < rootGame.coords.y // И она выше текущей «корневой игры»
            || ( // или левее (приквел?) и дата выхода раньше
              gameInfo.coords.y === rootGame.coords.y
              && gameInfo.coords.x < rootGame.coords.x
              && gameInfo.game.release_date < rootGame.game.release_date
            )
          )
        ) {
          rootGame = gameInfo
        }
      })
      const deltaX = 0 - rootGame.coords.x
      const deltaY = 0 - rootGame.coords.y
      batch.forEach(gameInfo => {
        gameInfo.coords.x += deltaX
        gameInfo.coords.y += deltaY
      })
      this.roots.push(rootGame.game)
    })
  }
}
